import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { KontrollterminService } from '../../services/kontrolltermin.service';
import { Kontrolltermin, KontrollIndikator } from '../../services/kontrolltermin.service';
import { ReportService, ReportInfo, ReportStatus } from '../../services/report.service';
import { environment } from '../../../environments/environment';
import { getSession } from '../../auth/storage';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { DialogIndikatorEditComponent } from '../dialogs/dialog-indikator-edit/dialog-indikator-edit.component';
import { EmailPreviewDialogComponent } from '../dialogs/email-preview-dialog/email-preview-dialog.component';

interface ReportContact {
  email: string;
  name: string;
  anrede: string;
  selected?: boolean;
}

@Component({
  selector: 'app-kontrolltermin-edit',
  templateUrl: './kontrolltermin-edit.component.html',
  styleUrls: ['./kontrolltermin-edit.component.scss']
})
export class KontrollterminEditComponent implements OnInit, OnDestroy {
  kontrolltermin: Kontrolltermin | null = null;
  kontrollterminId: string = '';
  isLoading = true;
  isEditMode = false;
  isFreigabeEditMode = false; // Edit mode for freigabe view
  isSaving = false;
  berichtEmail: string = '';
  
  // Email sending interface
  savedContacts: ReportContact[] = [];
  additionalContact: ReportContact = { email: '', name: '', anrede: '' };
  selectedRecipients: string[] = [];
  
  // Report tracking
  reportInfo: ReportInfo | null = null;
  reportStatus: ReportStatus | null = null;
  hasReportGenerated = false;
  hasReportSent = false;
  isGeneratingReport = false;
  isSendingReport = false;
  
  // Status tracking
  sourceStatus: string = ''; // Track source status from tab
  currentStatus: string = ''; // Track kontrolltermin's current status
  
  // Form
  kontrollterminForm: FormGroup;
  
  // Dropdown options
  einsatztypOptions = environment.kontrolltermin_einsatztyp;
  statusOptions = environment.kontrolltermin_status;
  befallAmpelOptions = environment.kontroll_indikator_befall_ampel;
  
  // Weekday options
  wochentagOptions = [
    { value: 'Montag', label: 'Montag' },
    { value: 'Dienstag', label: 'Dienstag' },
    { value: 'Mittwoch', label: 'Mittwoch' },
    { value: 'Donnerstag', label: 'Donnerstag' },
    { value: 'Freitag', label: 'Freitag' },
    { value: 'Samstag', label: 'Samstag' },
    { value: 'Sonntag', label: 'Sonntag' }
  ];
  
  // Month options
  monatOptions = [
    { value: 'Januar 2024', label: 'Januar 2024' },
    { value: 'Februar 2024', label: 'Februar 2024' },
    { value: 'März 2024', label: 'März 2024' },
    { value: 'April 2024', label: 'April 2024' },
    { value: 'Mai 2024', label: 'Mai 2024' },
    { value: 'Juni 2024', label: 'Juni 2024' },
    { value: 'Juli 2024', label: 'Juli 2024' },
    { value: 'August 2024', label: 'August 2024' },
    { value: 'September 2024', label: 'September 2024' },
    { value: 'Oktober 2024', label: 'Oktober 2024' },
    { value: 'November 2024', label: 'November 2024' },
    { value: 'Dezember 2024', label: 'Dezember 2024' }
  ];
  
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private kontrollterminService: KontrollterminService,
    private reportService: ReportService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    this.kontrollterminForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.kontrollterminId = params['id'];
      if (this.kontrollterminId) {
        this.loadKontrolltermin();
      }
    });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.isEditMode = params['mode'] === 'edit';
      this.sourceStatus = params['sourceStatus'] || 'unknown';
      
      console.log('🎯 EDIT COMPONENT LOADED:');
      console.log('📊 Source Status (from tab):', this.sourceStatus);
      console.log('📋 Mode:', this.isEditMode ? 'edit' : 'view');
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadKontrolltermin(): void {
    this.isLoading = true;
    
    // Load kontrolltermin and related data
    this.kontrollterminService.getKontrolltermin(this.kontrollterminId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (kontrolltermin) => {
        console.log('📋 Kontrolltermin Loaded:');
        console.log('📊 Current Status ID:', kontrolltermin.status_id);
        console.log('📊 Current Status Key:', this.getStatusKeyFromId(kontrolltermin.status_id));
        console.log('🎯 Source vs Current Match:', this.sourceStatus === this.getStatusKeyFromId(kontrolltermin.status_id));
        console.log('📊 Kontrolltermin data:', kontrolltermin);
        console.log('📊 Kunde data:', kontrolltermin.kunde);
        console.log('📊 Standort data:', kontrolltermin.standort);
        console.log('📊 Techniker data:', kontrolltermin.techniker);
        
        this.kontrolltermin = kontrolltermin;
        this.currentStatus = this.getStatusKeyFromId(kontrolltermin.status_id);
        
        // Handle both camelCase and snake_case naming conventions
        const kontrollIndikatoren = kontrolltermin.kontrollIndikatoren || kontrolltermin.kontroll_indikatoren;
        console.log('📊 kontrollIndikatoren in response:', kontrollIndikatoren);
        console.log('📊 kontrollIndikatoren type:', typeof kontrollIndikatoren);
        
        // Normalize the naming convention for consistency
        if (kontrolltermin.kontroll_indikatoren && !kontrolltermin.kontrollIndikatoren) {
          kontrolltermin.kontrollIndikatoren = kontrolltermin.kontroll_indikatoren;
        }
        
        // Initialize bericht email with contact email
        this.berichtEmail = this.getContactEmail();
        
        // Load saved contacts for email sending interface
        this.loadSavedContacts();
        
        // Check report status for abgeschlossen status
        if (this.currentStatus === 'abgeschlossen') {
          this.checkReportStatus();
        }
        
        this.setupStatusSpecificBehavior();
        
        
        // KontrollIndikatoren are loaded with the kontrolltermin via the main API
        console.log('📊 KontrollIndikatoren from kontrolltermin:', this.kontrolltermin.kontrollIndikatoren);
        console.log('📊 KontrollIndikatoren count:', this.kontrolltermin.kontrollIndikatoren?.length || 0);
        
        // Check if freigabe is already loaded from the main API call
        if (this.kontrolltermin!.status_id === 7 || this.kontrolltermin!.status_id === 8) {
          if (this.kontrolltermin!.freigabe) {
            // Freigabe already loaded from main API call
            console.log('✅ Freigabe already loaded from main API call');
            this.initializeForm();
            this.isLoading = false;
          } else {
            // Load freigabe separately if not included in main call
            console.log('🔄 Loading freigabe separately...');
            this.kontrollterminService.getFreigabeByKontrolltermin(this.kontrollterminId).pipe(
              takeUntil(this.destroy$)
            ).subscribe({
              next: (freigabe) => {
                this.kontrolltermin!.freigabe = freigabe || undefined; // Convert null to undefined for TypeScript
                this.initializeForm();
                this.isLoading = false;
              },
              error: (error) => {
                console.error('Error loading freigabe:', error);
                // No freigabe found, which is fine
                this.kontrolltermin!.freigabe = undefined;
                this.initializeForm();
                this.isLoading = false;
              }
            });
          }
        } else {
          // For other statuses (like geplant), skip freigabe loading
          console.log('Skipping freigabe loading for status:', this.kontrolltermin!.status_id);
          this.initializeForm();
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('Error loading kontrolltermin:', error);
        this.isLoading = false;
        
        // Check if it's a 404 or "not found" error
        if (error.status === 404 || error.status === 500) {
          this.snackBar.open('Kontrolltermin nicht gefunden', 'Schließen', { duration: 5000 });
          // Navigate back to kontrolltermine list after a short delay
          setTimeout(() => {
            this.router.navigate(['/kontrolltermine']);
          }, 2000);
        } else {
          this.snackBar.open('Fehler beim Laden des Kontrolltermins', 'Schließen', { duration: 3000 });
        }
      }
    });
  }

  initializeForm(): void {
    if (!this.kontrolltermin) return;

    this.kontrollterminForm = this.fb.group({
      // Section A: Basic Data
      einsatznummer: [{ value: this.kontrolltermin.einsatznummer, disabled: true }],
      standort_id: [this.kontrolltermin.standort_id, Validators.required],
      projektaufgabe_id: [this.kontrolltermin.projektaufgabe_id, Validators.required],
      kunde_id: [this.kontrolltermin.kunde_id, Validators.required],
      techniker_id: [this.kontrolltermin.techniker_id, Validators.required],
      einsatztyp: [this.kontrolltermin.einsatztyp, Validators.required],
      status_id: [this.kontrolltermin.status_id, Validators.required],
      datum_durchfuerung: [this.kontrolltermin.datum_durchfuerung, Validators.required],
      monat_plan: [this.kontrolltermin.monat_plan],
      wochentag_plan: [this.kontrolltermin.wochentag_plan],
      soll_zeit: [this.kontrolltermin.soll_zeit],
      ist_zeit: [this.kontrolltermin.ist_zeit],
      anmerkungen: [this.kontrolltermin.anmerkungen],
      zusatz_massnahme: [this.kontrolltermin.zusatz_massnahme],
      
      // Section C: Freigabe Data
      freigabe: this.fb.group({
        freigegeben: [this.kontrolltermin.freigabe?.freigegeben || false],
        freigabe_datum: [this.kontrolltermin.freigabe?.freigabe_datum || null],
        freigabe_durch: [this.kontrolltermin.freigabe?.freigabe_durch || null],
        anmerkungen_freigabe: [this.kontrolltermin.freigabe?.anmerkungen_freigabe || ''],
        versand_datum: [this.kontrolltermin.freigabe?.versand_datum || null],
        auswertung_link: [this.kontrolltermin.freigabe?.auswertung_link || ''],
        bericht_link: [this.kontrolltermin.freigabe?.bericht_link || '']
      }),
      
      // Section B: Indicators (will be handled separately)
              kontrollIndikatoren: this.fb.array([])
    });

    // Initialize indicators form array
    if (this.kontrolltermin.kontrollIndikatoren) {
      this.initializeIndicatorsForm();
    }
  }

  initializeIndicatorsForm(): void {
    const indicatorsArray = this.kontrollterminForm.get('kontrollIndikatoren') as FormArray;
    indicatorsArray.clear();

    this.kontrolltermin?.kontrollIndikatoren?.forEach(indikator => {
      indicatorsArray.push(this.fb.group({
        id: [indikator.id],
        gebaeude_id: [indikator.gebaeude_id],
        gebaeude_name: [indikator.gebaeude_name],
        sektion_id: [indikator.sektion_id],
        sektion_name: [indikator.sektion_name],
        room_id: [indikator.room_id],
        room_name: [indikator.room_name],
        indikator_id: [indikator.indikator_id],
        indikator_name: [indikator.indikator_name],
        wert: [indikator.wert],
        befall: [indikator.befall],
        befall_status_id: [indikator.befall_status_id],
        problem: [indikator.problem],
        massnahme: [indikator.massnahme],
        checked: [indikator.checked],
        checked_by: [indikator.checked_by],
        anmerkung: [indikator.anmerkung],
        interne_notiz: [indikator.interne_notiz]
      }));
    });
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    this.updateUrl();
  }

  updateUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { mode: this.isEditMode ? 'edit' : 'view' },
      queryParamsHandling: 'merge'
    });
  }

  saveKontrolltermin(): void {
    if (!this.kontrolltermin || !this.kontrollterminForm.valid) return;

    this.isSaving = true;
    const formValue = this.kontrollterminForm.getRawValue();
    
    // Get the current kontrollIndikatoren (either from main API or separately loaded)
    const currentKontrollIndikatoren = this.kontrolltermin.kontrollIndikatoren || [];
    
    // Merge form data with existing kontrolltermin
    const updatedKontrolltermin = {
      ...this.kontrolltermin,
      ...formValue,
      // Preserve existing kontrollIndikatoren (they're not part of the form)
      kontrollIndikatoren: currentKontrollIndikatoren
    };

    this.kontrollterminService.updateKontrolltermin(this.kontrollterminId, updatedKontrolltermin).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (updatedKontrolltermin) => {
        this.isSaving = false;
        this.isEditMode = false;
        this.updateUrl();
        
        // Update local data with the response instead of reloading
        this.kontrolltermin = {
          ...updatedKontrolltermin,
          // Preserve existing kontrollIndikatoren if not in response
          kontrollIndikatoren: updatedKontrolltermin.kontrollIndikatoren || currentKontrollIndikatoren
        };
        
        // Update the form with the new data
        this.kontrollterminForm.patchValue(updatedKontrolltermin);
        
        this.snackBar.open('Kontrolltermin erfolgreich gespeichert', 'Schließen', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error updating kontrolltermin:', error);
        this.isSaving = false;
        this.snackBar.open('Fehler beim Speichern des Kontrolltermins', 'Schließen', { duration: 3000 });
      }
    });
  }

  cancelEdit(): void {
    this.isEditMode = false;
    this.updateUrl();
    this.loadKontrolltermin(); // Reload to reset any changes
  }

  // Freigabe edit mode methods
  toggleFreigabeEditMode(): void {
    this.isFreigabeEditMode = !this.isFreigabeEditMode;
  }

  saveFreigabeEdits(): void {
    if (!this.kontrolltermin || !this.kontrollterminForm.valid) return;

    this.isSaving = true;
    const formValue = this.kontrollterminForm.getRawValue();
    
    // Get the current kontrollIndikatoren (preserve them)
    const currentKontrollIndikatoren = this.kontrolltermin.kontrollIndikatoren || [];
    
    // Merge form data with existing kontrolltermin
    const updatedKontrolltermin = {
      ...this.kontrolltermin,
      ...formValue,
      // Preserve existing kontrollIndikatoren (they're not part of the form)
      kontrollIndikatoren: currentKontrollIndikatoren
    };
    
    // Update kontrolltermin with edited values
    this.kontrollterminService.updateKontrolltermin(this.kontrolltermin.id, updatedKontrolltermin).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (updatedKontrolltermin) => {
        this.isSaving = false;
        this.isFreigabeEditMode = false;
        // Preserve kontrollIndikatoren in the response
        this.kontrolltermin = {
          ...updatedKontrolltermin,
          kontrollIndikatoren: currentKontrollIndikatoren
        };
        this.snackBar.open('Änderungen erfolgreich gespeichert', 'Schließen', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error updating kontrolltermin:', error);
        this.isSaving = false;
        this.snackBar.open('Fehler beim Speichern der Änderungen', 'Schließen', { duration: 3000 });
      }
    });
  }

  cancelFreigabeEdit(): void {
    this.isFreigabeEditMode = false;
    // Reset form to original values without reloading from server
    this.initializeForm();
  }

  goBack(): void {
    this.router.navigate(['/kontrolltermine']);
  }

  // Action Methods
  freigeben(): void {
    if (confirm('Möchten Sie diesen Kontrolltermin freigeben?')) {
      // TODO: Implement freigabe logic
      this.snackBar.open('Freigabe wird verarbeitet...', 'Schließen', { duration: 3000 });
    }
  }

  alsMailSenden(): void {
    if (confirm('Möchten Sie den Bericht als E-Mail senden?')) {
      // TODO: Implement email sending logic
      this.snackBar.open('E-Mail wird gesendet...', 'Schließen', { duration: 3000 });
    }
  }

  berichtErstellen(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open('Kein Kontrolltermin geladen', 'Schließen', { duration: 3000 });
      return;
    }

    this.isSaving = true;
    
    // Generate PDF report
    this.kontrollterminService.generatePDFReport(this.kontrolltermin.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: any) => {
        this.isSaving = false;
        
        // Create blob and download
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `kontrolltermin-${this.kontrolltermin!.einsatznummer}-${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        this.snackBar.open('PDF-Bericht erfolgreich generiert und heruntergeladen', 'Schließen', { duration: 3000 });
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error generating PDF report:', error);
        this.snackBar.open('Fehler bei der PDF-Generierung', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * Delete kontrolltermin (mark as gelöscht)
   */
  deleteKontrolltermin(): void {
    if (!this.kontrolltermin) return;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Kontrolltermin löschen',
        message: `Möchten Sie den Kontrolltermin #${this.kontrolltermin.einsatznummer} wirklich löschen?`,
        confirmText: 'Löschen',
        cancelText: 'Abbrechen',
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.performDelete();
      }
    });
  }

  /**
   * Perform the actual delete operation
   */
  private performDelete(): void {
    if (!this.kontrolltermin) return;

    this.isSaving = true;
    
    // Update status to "gelöscht" (status_id: 1)
    const updateData = {
      status_id: 1 // gelöscht status
    };

    this.kontrollterminService.updateKontrolltermin(this.kontrolltermin.id, updateData).subscribe({
      next: (response) => {
        this.snackBar.open('Kontrolltermin wurde gelöscht', 'Schließen', { 
          duration: 3000,
          panelClass: ['success-snackbar']
        });
        
        // Navigate back to the list
        this.router.navigate(['/kontrolltermine']);
      },
      error: (error) => {
        console.error('Error deleting kontrolltermin:', error);
        this.snackBar.open('Fehler beim Löschen des Kontrolltermins', 'Schließen', { 
          duration: 3000,
          panelClass: ['error-snackbar']
        });
        this.isSaving = false;
      }
    });
  }

  // Helper Methods
  getBefallColor(befall: number | null): string {
    if (befall === 1) return 'text-red-600 bg-red-50';
    if (befall === 0) return 'text-green-600 bg-green-50';
    return 'text-gray-600 bg-gray-50';
  }

  getBefallText(befall: number | null): string {
    if (befall === 1) return 'Ja';
    if (befall === 0) return 'Nein';
    return 'Unbekannt';
  }

  getBefallStatusColor(statusId: number | null): string {
    const status = this.befallAmpelOptions.find(s => s.id === statusId);
    return status ? status.color : '#6c757d';
  }

  getBefallStatusText(statusId: number | null): string {
    const status = this.befallAmpelOptions.find(s => s.id === statusId);
    return status ? status.label : 'Unbekannt';
  }

  // New methods for status badges (OK/Warnung/Kritisch)
  getStatusBadgeClass(statusId: string | number | null): string {
    const id = String(statusId);
    if (id === '3') return 'bg-green-500'; // OK
    if (id === '2') return 'bg-orange-500'; // Warnung  
    if (id === '1') return 'bg-red-500'; // Kritisch
    return 'bg-gray-500'; // Default
  }

  getStatusBadgeText(statusId: string | number | null): string {
    const id = String(statusId);
    if (id === '3') return 'OK';
    if (id === '2') return 'Warnung';
    if (id === '1') return 'Kritisch';
    return 'Unbekannt';
  }

  // Helper method to check if indikator has been checked
  isIndikatorChecked(indikator: any): boolean {
    // An indikator is considered checked ONLY if BOTH conditions are met:
    // 1. It has a checked date (not null, not undefined, not empty string)
    // 2. It has a checked_by user (not null, not undefined, not empty string)
    const hasCheckedDate = indikator.checked && 
                          indikator.checked !== null && 
                          indikator.checked !== undefined && 
                          indikator.checked !== '' &&
                          indikator.checked !== 'null';
    
    const hasCheckedBy = indikator.checked_by && 
                        indikator.checked_by !== null && 
                        indikator.checked_by !== undefined && 
                        indikator.checked_by !== '' &&
                        indikator.checked_by !== 'null';
    
    const isChecked = hasCheckedDate && hasCheckedBy;
    
    return isChecked;
  }

  getStatusText(statusId: number | null): string {
    const status = this.statusOptions.find(s => s.id === statusId);
    return status ? status.label : 'Unbekannt';
  }

  getEinsatztypText(einsatztyp: string | null): string {
    const type = this.einsatztypOptions.find(t => t.value === einsatztyp);
    return type ? type.label : 'Unbekannt';
  }

  getTechnikerName(techniker: any): string {
    if (!techniker) return 'Nicht zugewiesen';
    if (typeof techniker === 'string') return techniker || 'Nicht zugewiesen';
    if (techniker.full_name) return techniker.full_name;
    if (techniker.display_name) return techniker.display_name;
    const first = techniker.vorname || techniker.first_name || '';
    const last = techniker.nachname || techniker.last_name || '';
    const composed = `${first} ${last}`.trim();
    if (composed) return composed;
    if (techniker.name) return techniker.name;
    if (techniker.nummer) return `#${techniker.nummer}`;
    return 'Nicht zugewiesen';
  }

  /**
   * Get status key from status ID
   */
  getStatusKeyFromId(statusId: number): string {
    const statusMap: {[key: number]: string} = {
      1: 'geloescht',
      2: 'abgebrochen', 
      3: 'fehlerhaft',
      4: 'geplant',
      5: 'aktuell',
      6: 'bearbeitung',
      7: 'freigabe',
      8: 'abgeschlossen'
    };
    return statusMap[statusId] || 'unknown';
  }

  /**
   * Setup status-specific behavior
   */
  setupStatusSpecificBehavior(): void {
    console.log('🔧 Setting up status-specific behavior:');
    console.log('📊 Source Status:', this.sourceStatus);
    console.log('📊 Current Status:', this.currentStatus);
    
    // Different behavior based on source status
    switch(this.sourceStatus) {
      case 'geloescht':
        console.log('🗑️ Setting up GELÖSCHT view behavior');
        this.setupGeloeschtBehavior();
        break;
      case 'abgebrochen':
        console.log('❌ Setting up ABGEBROCHEN view behavior');
        this.setupAbgebrochenBehavior();
        break;
      case 'fehlerhaft':
        console.log('⚠️ Setting up FEHLERHAFT view behavior');
        this.setupFehlerhaftBehavior();
        break;
      case 'geplant':
        console.log('📅 Setting up GEPLANT view behavior');
        this.setupGeplantBehavior();
        break;
      case 'aktuell':
        console.log('🔄 Setting up AKTUELL view behavior');
        this.setupAktuellBehavior();
        break;
      case 'bearbeitung':
        console.log('⚙️ Setting up BEARBEITUNG view behavior');
        this.setupBearbeitungBehavior();
        break;
      case 'freigabe':
        console.log('✅ Setting up FREIGABE view behavior');
        this.setupFreigabeBehavior();
        break;
      case 'abgeschlossen':
        console.log('🏁 Setting up ABGESCHLOSSEN view behavior');
        this.setupAbgeschlossenBehavior();
        break;
    }
  }

  // Status-specific behavior methods
  setupGeloeschtBehavior(): void {
    console.log('🗑️ GELÖSCHT: Limited editing, focus on restoration');
  }

  setupAbgebrochenBehavior(): void {
    console.log('❌ ABGEBROCHEN: Show cancellation details, reschedule options');
  }

  setupFehlerhaftBehavior(): void {
    console.log('⚠️ FEHLERHAFT: Show error details, correction options');
  }

  setupGeplantBehavior(): void {
    console.log('📅 GEPLANT: Full editing, scheduling options');
  }

  setupAktuellBehavior(): void {
    console.log('🔄 AKTUELL: Active work, progress tracking');
  }

  setupBearbeitungBehavior(): void {
    console.log('⚙️ BEARBEITUNG: Work in progress, completion options');
  }

  setupFreigabeBehavior(): void {
    console.log('✅ FREIGABE: Approval workflow, final checks');
    console.log('🔍 Current Status:', this.currentStatus);
    console.log('🔍 Freigabe exists:', !!this.kontrolltermin?.freigabe);
    console.log('🔍 Freigegeben value:', this.kontrolltermin?.freigabe?.freigegeben);
    console.log('🔍 Freigegeben type:', typeof this.kontrolltermin?.freigabe?.freigegeben);
    console.log('🔍 Should show freigabe form (always on freigabe view):', this.currentStatus === 'freigabe');
    console.log('🔍 Should show freigabe details (only on abgeschlossen):', this.currentStatus === 'abgeschlossen');
    console.log('🔍 Form initialized:', !!this.kontrollterminForm);
    console.log('🔍 Freigabe form group:', !!this.freigabeFormGroup);
    console.log('🔍 Freigabe form group valid:', this.freigabeFormGroup?.valid);
    console.log('🔍 FALLBACK: Always showing freigabe button on freigabe view for retry capability');
  }

  setupAbgeschlossenBehavior(): void {
    console.log('🏁 ABGESCHLOSSEN: Read-only with reactivation options');
    console.log('🔍 Current status after setupAbgeschlossenBehavior:', this.currentStatus);
    console.log('🔍 Should show report block:', this.currentStatus === 'abgeschlossen');
  }

  /**
   * Handle status change from child components
   */
  onStatusChanged(event: any): void {
    console.log('🔄 Status change received:', event);
    const { newStatus, kontrolltermin } = event;
    
    // TODO: Implement status change logic
    // This would typically call the API to update the kontrolltermin status
    console.log(`📊 Changing status from ${this.currentStatus} to ${newStatus}`);
    
    // For now, just show a message
    this.snackBar.open(`Status wird geändert zu: ${newStatus}`, 'Schließen', { duration: 3000 });
  }

  /**
   * Handle header field changes
   */
  onHeaderFieldChanged(event: {field: string, value: any}): void {
    if (event.field === 'soll_zeit' || event.field === 'ist_zeit') {
      // Update the form control
      this.kontrollterminForm.patchValue({
        [event.field]: event.value
      });
      // Update the kontrolltermin object
      if (this.kontrolltermin) {
        this.kontrolltermin[event.field] = event.value;
      }
      console.log(`📊 Header field changed: ${event.field} = ${event.value}`);
    }
  }

  // Form getters
  get indicatorsFormArray(): FormArray {
    return this.kontrollterminForm.get('kontrollIndikatoren') as FormArray;
  }

  get freigabeFormGroup(): FormGroup | null {
    return this.kontrollterminForm?.get('freigabe') as FormGroup || null;
  }

  getEditableFields(): string[] {
    if (this.sourceStatus === 'bearbeitung') {
      return ['soll_zeit', 'ist_zeit'];
    } else if (this.sourceStatus === 'freigabe' && this.isFreigabeEditMode) {
      return ['status_id', 'techniker_id', 'datum_durchfuerung', 'soll_zeit', 'ist_zeit'];
    }
    return [];
  }

  /**
   * Go back to kontrolltermine list
   */
  goBackToList(): void {
    this.router.navigate(['/kontrolltermine']);
  }

  editIndikator(indikator: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    console.log('🔧 Opening indikator edit dialog for:', indikator);
    
    const dialogRef = this.dialog.open(DialogIndikatorEditComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      data: {
        indikator: { ...indikator }, // Create a copy to avoid direct mutation
        befallAmpelOptions: this.befallAmpelOptions,
        currentStatus: this.currentStatus, // Pass current kontrolltermin status
        isViewMode: this.currentStatus === 'freigabe' || this.currentStatus === 'abgeschlossen'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('💾 Indikator updated:', result);
        // Update the indikator in the kontrolltermin object
        const index = this.kontrolltermin?.kontrollIndikatoren?.findIndex(ki => ki.id === result.id);
        if (index !== undefined && index >= 0 && this.kontrolltermin?.kontrollIndikatoren) {
          this.kontrolltermin.kontrollIndikatoren[index] = result;
          
          // Also update the form array to trigger UI refresh
          const indicatorsArray = this.kontrollterminForm.get('kontrollIndikatoren') as FormArray;
          if (indicatorsArray && indicatorsArray.at(index)) {
            indicatorsArray.at(index).patchValue(result);
          }
          
          // Trigger change detection to update the UI
          this.cdr.detectChanges();
        }
        this.snackBar.open('Indikator erfolgreich aktualisiert', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * Complete the control (Kontrolle abschließen) - set status to Freigabe
   */
  kontrolleAbschliessen(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open('Kein Kontrolltermin geladen', 'Schließen', { duration: 3000 });
      return;
    }

    // Show confirmation dialog
    this.showCompletionConfirmation();
  }

  /**
   * Show confirmation dialog for completing the control
   */
  private showCompletionConfirmation(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Kontrolle abschließen',
        message: 'Möchten Sie die Kontrolle wirklich abschließen? Der Status wird auf "Freigabe" gesetzt und der Kontrolltermin kann nicht mehr bearbeitet werden.',
        confirmText: 'Abschließen',
        cancelText: 'Abbrechen',
        confirmColor: 'primary'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.processControlCompletion();
      }
    });
  }

  /**
   * Process the control completion
   */
  private processControlCompletion(): void {
    this.isSaving = true;
    
    // Update the status to Freigabe (status_id = 6)
    const updatedKontrolltermin = {
      ...this.kontrolltermin,
      status_id: 6 // Freigabe status
    };

    this.kontrollterminService.updateKontrolltermin(this.kontrollterminId, updatedKontrolltermin)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSaving = false;
          this.snackBar.open('Kontrolle erfolgreich abgeschlossen', 'Schließen', { duration: 3000 });
          
          // Navigate back to the list
          this.router.navigate(['/kontrolltermine']);
        },
        error: (error) => {
          this.isSaving = false;
          console.error('Error completing control:', error);
          this.snackBar.open('Fehler beim Abschließen der Kontrolle', 'Schließen', { duration: 3000 });
        }
      });
  }

  getProblemItems(indikator: any): any[] {
    try {
      if (indikator.problem) {
        // Check if it's JSON format
        if (indikator.problem.startsWith('[') || indikator.problem.startsWith('{')) {
          return JSON.parse(indikator.problem);
        } else {
          // It's plain text, return as single item
          return [{
            id: 'text-' + Date.now(),
            name: indikator.problem,
            description: 'Text input'
          }];
        }
      }
    } catch (e) {
      console.log('Error parsing problem data:', e);
      // Return as plain text if JSON parsing fails
      return [{
        id: 'text-' + Date.now(),
        name: indikator.problem,
        description: 'Text input'
      }];
    }
    return [];
  }

  getMassnahmeItems(indikator: any): any[] {
    try {
      if (indikator.massnahme) {
        // Check if it's JSON format
        if (indikator.massnahme.startsWith('[') || indikator.massnahme.startsWith('{')) {
          return JSON.parse(indikator.massnahme);
        } else {
          // It's plain text, return as single item
          return [{
            id: 'text-' + Date.now(),
            name: indikator.massnahme,
            description: 'Text input'
          }];
        }
      }
    } catch (e) {
      console.log('Error parsing massnahme data:', e);
      // Return as plain text if JSON parsing fails
      return [{
        id: 'text-' + Date.now(),
        name: indikator.massnahme,
        description: 'Text input'
      }];
    }
    return [];
  }

  getArtikelItems(indikator: any): any[] {
    try {
      if (indikator.interne_notiz) {
        // Check if it's JSON format
        if (indikator.interne_notiz.startsWith('[') || indikator.interne_notiz.startsWith('{')) {
          return JSON.parse(indikator.interne_notiz);
        } else {
          // It's plain text, return as single item
          return [{
            id: 'text-' + Date.now(),
            name: indikator.interne_notiz,
            description: 'Text input'
          }];
        }
      }
    } catch (e) {
      console.log('Error parsing artikel data:', e);
      // Return as plain text if JSON parsing fails
      return [{
        id: 'text-' + Date.now(),
        name: indikator.interne_notiz,
        description: 'Text input'
      }];
    }
    return [];
  }

  /**
   * Complete the bearbeitung (Abschließen)
   */
  abschliessen(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open('Kein Kontrolltermin geladen', 'Schließen', { duration: 3000 });
      return;
    }

    // Show confirmation dialog
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Bearbeitung abschließen',
        message: 'Möchten Sie die Bearbeitung wirklich abschließen? Der Status wird auf "Freigabe" gesetzt.',
        confirmText: 'Abschließen',
        cancelText: 'Abbrechen',
        confirmColor: 'primary'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.completeBearbeitung();
      }
    });
  }

  /**
   * Complete the bearbeitung and move to freigabe status
   */
  private completeBearbeitung(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open('Kein Kontrolltermin geladen', 'Schließen', { duration: 3000 });
      return;
    }

    this.isSaving = true;

    // Get current form data
    const formValue = this.kontrollterminForm.getRawValue();
    
    // Get current kontrollIndikatoren (preserve them)
    const currentKontrollIndikatoren = this.kontrolltermin.kontrollIndikatoren || [];
    
    // Create updated kontrolltermin object
    const updatedKontrolltermin = {
      ...this.kontrolltermin,
      ...formValue,
      // Update status to freigabe (status 7)
      status_id: 7,
      // Update timestamp
      updated_at: new Date().toISOString(),
      // Preserve existing kontrollIndikatoren
      kontrollIndikatoren: currentKontrollIndikatoren
    };

    console.log('🔄 Completing bearbeitung, updating to freigabe status...');
    console.log('📊 Updated kontrolltermin:', updatedKontrolltermin);

    this.kontrollterminService.updateKontrolltermin(this.kontrolltermin.id, updatedKontrolltermin).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isSaving = false;
        console.log('✅ Bearbeitung completed successfully:', response);
        
        // Update local data
        this.kontrolltermin = {
          ...response,
          // Preserve existing kontrollIndikatoren if not in response
          kontrollIndikatoren: response.kontrollIndikatoren || currentKontrollIndikatoren
        };
        
        // Update current status
        this.currentStatus = this.getStatusKeyFromId(response.status_id);
        
        // Update form with new data
        this.kontrollterminForm.patchValue(response);
        
        // Update URL to reflect new status
        this.updateUrl();
        
        this.snackBar.open('Bearbeitung abgeschlossen - Status: Freigabe', 'Schließen', { duration: 3000 });
        
        // Navigate back to kontrolltermine list
        setTimeout(() => {
          this.router.navigate(['/kontrolltermine']);
        }, 1000);
      },
      error: (error) => {
        this.isSaving = false;
        console.error('❌ Error completing bearbeitung:', error);
        this.snackBar.open('Fehler beim Abschließen der Bearbeitung', 'Schließen', { duration: 3000 });
      }
    });
  }



  getIndikatorDisplayNumber(indikator: any): string {
    const n = indikator?.indikator?.nummer
      ?? indikator?.indikator_nummer
      ?? indikator?.nummer
      ?? indikator?.indikator_id;
    const num = Number(n);
    if (isFinite(num)) {
      return '#' + num.toString().padStart(3, '0');
    }
    return '#—';
  }

  getSymbolUrlFromIndicator(indicator: any): string | null {
    const symbolId = indicator?.indikator?.symbol_id;
    if (!symbolId) return null;
    const key = (environment as any).indikatorSymbolMap?.[symbolId];
    if (!key) return null;
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.svg`;
  }

  getIndikatorCode(indicator: any): string {
    return indicator?.indikator?.code || '';
  }

  getFormattedSchwellenwert(indicator: any): string {
    const schwellenwert = indicator?.indikator?.schwellenwert;
    if (!schwellenwert) return '-';
    
    // Convert to number and format as integer
    const num = Number(schwellenwert);
    return isFinite(num) ? Math.round(num).toString() : '-';
  }

  private getSchwellenwertFromIndikator(indikator: any): number {
    const sw = indikator?.indikator?.schwellenwert;
    const num = Number(sw);
    return isFinite(num) ? num : 0;
  }

  getComputedBefall(indikator: any): 0 | 1 {
    const wert = Number(indikator?.wert);
    const schwellenwert = this.getSchwellenwertFromIndikator(indikator);
    if (!isFinite(wert)) return 0;
    return wert > schwellenwert ? 1 : 0;
  }

  getComputedStatusId(indikator: any): '1' | '2' | '3' {
    const wert = Number(indikator?.wert);
    const schwellenwert = this.getSchwellenwertFromIndikator(indikator);
    if (!isFinite(wert)) return '2';
    if (wert === 0) return '3'; // green
    if (wert > schwellenwert) return '1'; // red
    return '2'; // orange
  }

  getCurrentUser(): any {
    const session = getSession();
    return session.user || null;
  }

  getCurrentUserName(): string {
    const session = getSession();
    if (!session) return 'Unbekannter Benutzer';
    
    // Try different session properties for user name
    if (session.dienstname) return session.dienstname;
    if (session.username) return session.username;
    if (session.user?.dienstname) return session.user.dienstname;
    if (session.user?.username) return session.user.username;
    if (session.user?.full_name) return session.user.full_name;
    if (session.user?.display_name) return session.user.display_name;
    
    return 'Unbekannter Benutzer';
  }

  getContactEmail(): string {
    // Use the dedicated bericht email from standort
    if (this.kontrolltermin?.standort?.bericht_email) {
      return this.kontrolltermin.standort.bericht_email;
    }
    
    // Fallback to placeholder if no bericht email is set
    if (this.kontrolltermin?.standort?.kunde?.suchbegriff) {
      return `contact@${this.kontrolltermin.standort.kunde.suchbegriff.toLowerCase().replace(/\s+/g, '')}.com`;
    }
    
    return '';
  }

  /**
   * Load saved contacts from standort
   */
  loadSavedContacts(): void {
    console.log('🔍 Loading saved contacts...');
    console.log('🔍 Standort bericht_email:', this.kontrolltermin?.standort?.bericht_email);
    
    if (this.kontrolltermin?.standort?.bericht_email) {
      try {
        let contacts: ReportContact[] = [];
        
        if (Array.isArray(this.kontrolltermin.standort.bericht_email)) {
          contacts = this.kontrolltermin.standort.bericht_email;
          console.log('✅ Loaded array format contacts:', contacts);
        } else if (typeof this.kontrolltermin.standort.bericht_email === 'string') {
          // Check if it's JSON string
          if (this.kontrolltermin.standort.bericht_email.startsWith('[') || this.kontrolltermin.standort.bericht_email.startsWith('{')) {
            try {
              contacts = JSON.parse(this.kontrolltermin.standort.bericht_email);
              console.log('✅ Parsed JSON string contacts:', contacts);
            } catch (parseError) {
              console.error('Error parsing JSON string:', parseError);
              // Fallback to old string format
              contacts = [{
                email: this.kontrolltermin.standort.bericht_email,
                name: '',
                anrede: ''
              }];
            }
          } else {
            // Handle old string format (backward compatibility)
            contacts = [{
              email: this.kontrolltermin.standort.bericht_email,
              name: '',
              anrede: ''
            }];
            console.log('✅ Loaded old string format contact:', contacts);
          }
        }
        
        // Add selection state to each contact (all selected by default)
        this.savedContacts = contacts.map(contact => ({
          ...contact,
          selected: true
        }));
        
        console.log('✅ Final savedContacts:', this.savedContacts);
      } catch (e) {
        console.error('Error loading saved contacts:', e);
        this.savedContacts = [];
      }
    } else {
      this.savedContacts = [];
    }
  }

  /**
   * Get selected email addresses for sending
   */
  getSelectedEmailAddresses(): string[] {
    const emails: string[] = [];
    
    // Add selected saved contacts
    this.savedContacts.forEach(contact => {
      if (contact.selected && contact.email) {
        emails.push(contact.email);
      }
    });
    
    // Add additional contact if email provided
    if (this.additionalContact.email) {
      emails.push(this.additionalContact.email);
    }
    
    console.log('📧 Selected email addresses:', emails);
    return emails;
  }

  /**
   * Prepare recipients data for API
   */
  private prepareRecipientsData(): any[] {
    const recipients: any[] = [];
    
    // Add selected saved contacts
    this.savedContacts.forEach(contact => {
      if (contact.selected && contact.email) {
        recipients.push({
          email: contact.email,
          name: contact.name || '',
          anrede: contact.anrede || 'Sehr geehrte Damen und Herren,'
        });
      }
    });
    
    // Add additional contact if provided
    if (this.additionalContact.email) {
      recipients.push({
        email: this.additionalContact.email,
        name: this.additionalContact.name || '',
        anrede: this.additionalContact.anrede || 'Sehr geehrte Damen und Herren,'
      });
    }
    
    return recipients;
  }

  /**
   * Send report to selected recipients
   */
  sendReportToSelected(): void {
    const recipients = this.prepareRecipientsData();
    
    if (recipients.length === 0) {
      this.snackBar.open('Bitte wählen Sie mindestens einen Empfänger aus', 'Schließen', { duration: 3000 });
      return;
    }
    
    console.log('📧 Sending report to recipients:', recipients);
    
    this.isSendingReport = true;
    
    this.reportService.sendReportWithRecipients(this.kontrolltermin!.id, recipients).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isSendingReport = false;
        this.snackBar.open(`Bericht erfolgreich an ${recipients.length} Empfänger versendet`, 'Schließen', { duration: 3000 });
        
        // Update report status
        this.checkReportStatus();
        console.log('✅ Report sent to recipients:', response);
      },
      error: (error) => {
        this.isSendingReport = false;
        this.snackBar.open('Fehler beim Versenden des Berichts', 'Schließen', { duration: 3000 });
        console.error('Error sending report:', error);
      }
    });
  }

  /**
   * Send report with multiple email addresses
   */
  sendReportWithAddresses(emailAddresses: string[]): void {
    if (!this.kontrolltermin) {
      this.snackBar.open('Kein Kontrolltermin geladen', 'Schließen', { duration: 3000 });
      return;
    }

    this.isSendingReport = true;
    
    this.reportService.sendReport(this.kontrolltermin.id, emailAddresses).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isSendingReport = false;
        this.snackBar.open(`Bericht erfolgreich an ${emailAddresses.length} Empfänger versendet`, 'Schließen', { duration: 3000 });
        
        // Update report status
        this.checkReportStatus();
        console.log('✅ Report sent to multiple recipients:', response);
      },
      error: (error) => {
        this.isSendingReport = false;
        this.snackBar.open('Fehler beim Versenden des Berichts', 'Schließen', { duration: 3000 });
        console.error('Error sending report:', error);
      }
    });
  }

  confirmFreigabe(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Kontrolltermin freigeben',
        message: 'Damit wird der Akt abgeschlossen und freigegeben. Möchten Sie fortfahren?',
        confirmText: 'Ja, freigeben',
        cancelText: 'Abbrechen',
        confirmColor: 'primary'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.executeFreigabe();
      }
    });
  }

  executeFreigabe(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open('Kein Kontrolltermin geladen', 'Schließen', { duration: 3000 });
      return;
    }

    this.isSaving = true;
    const session = getSession();
    const freigabeDurch = session?.fid || session?.userid; // Use the correct foreign ID
    const anmerkungen = this.freigabeFormGroup?.get('anmerkungen_freigabe')?.value || '';
    const freigabeDatum = new Date().toISOString();

    // Create freigabe data
    const freigabeData = {
      kontrolltermin_id: this.kontrolltermin.id,
      freigabe_durch: freigabeDurch,
      freigegeben: 1,
      freigabe_datum: freigabeDatum,
      anmerkungen_freigabe: anmerkungen
    };

    console.log('🔍 Freigabe data being sent:', freigabeData);

    // Create or update freigabe record
    this.kontrollterminService.createOrUpdateFreigabe(freigabeData).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (freigabeResponse) => {
        console.log('✅ Freigabe created/updated:', freigabeResponse);
        
        // Update kontrolltermin status to abgeschlossen (status_id = 8)
        const statusUpdateData = {
          status_id: 8, // abgeschlossen
          freigabe_id: freigabeResponse.id
        };

        // Chain the second request properly
        this.kontrollterminService.updateKontrolltermin(this.kontrolltermin!.id, statusUpdateData).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: (updatedKontrolltermin) => {
            this.isSaving = false;
            this.kontrolltermin = updatedKontrolltermin;
            
            // Update the current status to reflect the change
            this.currentStatus = this.getStatusKeyFromId(updatedKontrolltermin.status_id);
            console.log('✅ Status updated to:', this.currentStatus);
            
            // Setup the new behavior for abgeschlossen status
            this.setupAbgeschlossenBehavior();
            
            this.snackBar.open('Kontrolltermin erfolgreich freigegeben', 'Schließen', { duration: 3000 });
            console.log('✅ Kontrolltermin freigegeben successfully');
          },
          error: (error) => {
            console.error('Error updating kontrolltermin status:', error);
            this.isSaving = false;
            this.snackBar.open('Fehler beim Aktualisieren des Status', 'Schließen', { duration: 3000 });
          }
        });
      },
      error: (error) => {
        console.error('Error creating/updating freigabe:', error);
        this.isSaving = false;
        this.snackBar.open('Fehler beim Erstellen der Freigabe', 'Schließen', { duration: 3000 });
      }
    });
  }




  /**
   * Generate report for the kontrolltermin
   */
  generateReport(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open('Kein Kontrolltermin geladen', 'Schließen', { duration: 3000 });
      return;
    }

    this.isGeneratingReport = true;
    
    this.reportService.generateReport(this.kontrolltermin.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isGeneratingReport = false;
        this.snackBar.open('Bericht erfolgreich generiert', 'Schließen', { duration: 3000 });
        
        // Update report status
        this.checkReportStatus();
        console.log('✅ Report generated:', response);
      },
      error: (error) => {
        this.isGeneratingReport = false;
        this.snackBar.open('Fehler beim Generieren des Berichts', 'Schließen', { duration: 3000 });
        console.error('Error generating report:', error);
      }
    });
  }

  /**
   * View report (download)
   */
  viewReport(): void {
    if (!this.kontrolltermin || !this.reportInfo) {
      this.snackBar.open('Kein Bericht verfügbar', 'Schließen', { duration: 3000 });
      return;
    }

    this.reportService.downloadReportById(this.reportInfo.uuid);
  }

  /**
   * View report as HTML (for testing layout)
   */
  viewReportHtml(): void {
    if (!this.kontrolltermin || !this.reportInfo) {
      this.snackBar.open('Kein Bericht verfügbar', 'Schließen', { duration: 3000 });
      return;
    }

    // Use the stored report UUID to ensure we see the same content as the PDF
    const reportUrl = `${environment.serverUrl}/report/stored/${this.reportInfo.uuid}/html`;
    window.open(reportUrl, '_blank');
    this.snackBar.open('HTML Bericht wird geöffnet...', 'Schließen', { duration: 2000 });
  }

  /**
   * Send report via email
   */
  sendReport(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open('Kein Kontrolltermin geladen', 'Schließen', { duration: 3000 });
      return;
    }

    if (!this.berichtEmail) {
      this.snackBar.open('Bitte geben Sie eine E-Mail-Adresse ein', 'Schließen', { duration: 3000 });
      return;
    }

    this.isSendingReport = true;
    
    this.reportService.sendReport(this.kontrolltermin.id, [this.berichtEmail]).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.isSendingReport = false;
        this.snackBar.open('Bericht erfolgreich versendet', 'Schließen', { duration: 3000 });
        
        // Update report status
        this.checkReportStatus();
        console.log('✅ Report sent:', response);
      },
      error: (error) => {
        this.isSendingReport = false;
        this.snackBar.open('Fehler beim Versenden des Berichts', 'Schließen', { duration: 3000 });
        console.error('Error sending report:', error);
      }
    });
  }

  /**
   * Check report status and update UI
   */
  checkReportStatus(): void {
    if (!this.kontrolltermin) return;

    this.reportService.checkReportStatus(this.kontrolltermin.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (status) => {
        // Handle null response
        if (!status) {
          console.log('📊 No report status found - no report generated yet');
          this.reportStatus = null;
          this.hasReportGenerated = false;
          this.hasReportSent = false;
          return;
        }
        
        this.reportStatus = status;
        this.hasReportGenerated = status.has_report || false;
        this.hasReportSent = status.has_been_sent || false;
        
        // Load detailed report info if report exists
        if (status.has_report) {
          this.loadReportInfo();
        }
        
        console.log('📊 Report status updated:', status);
      },
      error: (error) => {
        console.error('Error checking report status:', error);
      }
    });
  }

  /**
   * Load detailed report information
   */
  loadReportInfo(): void {
    if (!this.kontrolltermin) return;

    this.reportService.getReportInfo(this.kontrolltermin.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (info) => {
        // Handle null response
        if (!info) {
          console.log('📋 No report info found');
          this.reportInfo = null;
          return;
        }
        
        this.reportInfo = info;
        console.log('📋 Report info loaded:', info);
      },
      error: (error) => {
        console.error('Error loading report info:', error);
        this.reportInfo = null;
      }
    });
  }

  /**
   * Get formatted file size
   */
  getFormattedFileSize(): string {
    return this.reportInfo ? this.reportService.formatFileSize(this.reportInfo.file_size) : '';
  }

  /**
   * Get formatted generation date
   */
  getFormattedGenerationDate(): string {
    return this.reportInfo ? this.reportService.formatDate(this.reportInfo.generated_at) : '';
  }

  /**
   * Get formatted send date
   */
  getFormattedSendDate(): string {
    return this.reportInfo && this.reportInfo.sent_at ? this.reportService.formatDate(this.reportInfo.sent_at) : '';
  }

  /**
   * Get generated by name
   */
  getGeneratedByName(): string {
    return this.reportInfo?.generated_by_name || 'Unbekannt';
  }

  /**
   * Get sent by name
   */
  getSentByName(): string {
    return this.reportInfo?.sent_by_name || 'Unbekannt';
  }

  /**
   * Get sent to emails
   */
  getSentToEmails(): string {
    return this.reportInfo?.sent_to || '';
  }

  /**
   * Preview email content
   */
  previewEmail(): void {
    if (!this.kontrolltermin) {
      this.snackBar.open('Kein Kontrolltermin geladen', 'Schließen', { duration: 3000 });
      return;
    }

    this.reportService.getEmailTemplate(this.kontrolltermin.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (template) => {
        const dialogRef = this.dialog.open(EmailPreviewDialogComponent, {
          width: '600px',
          data: {
            subject: template.subject,
            content: template.body
          }
        });
      },
      error: (error) => {
        this.snackBar.open('Fehler beim Laden der E-Mail-Vorschau', 'Schließen', { duration: 3000 });
        console.error('Error loading email template:', error);
      }
    });
  }

}
